// AI grooming preview generator.
//
// This is the single seam between the UI and the image-generation provider.
// The UI never talks to a provider directly — it calls the backend
// (`POST /api/preview/generate`), which holds the API key and picks the
// provider (see backend/ai_provider.py). Swapping providers is a server-side
// change only.
//
// Contract:
//   generateGroomingPreview({
//     originalImage, breed, style, bodyLength, faceStyle, earStyle, tailStyle, customInstructions
//   }) => Promise<{ previewImage, prompt, durationMs, isMock, provider, usage }>
//
// Errors are thrown as `GenerationError` with a stable `code`:
//   LIMIT_REACHED | INVALID_PHOTO | GENERATION_FAILED | SERVER_ERROR | NETWORK
import { apiFetch, getClientId, ApiError } from "@/lib/api";

const UPLOAD_MAX_DIM = 1024;

export class GenerationError extends Error {
  constructor(code, extra = {}) {
    super(code);
    this.code = code;
    Object.assign(this, extra);
  }
}

// Downscale before upload: cheaper, faster, and identity is preserved fine at ~1k px.
async function prepareImage(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, UPLOAD_MAX_DIM / Math.max(img.width, img.height));
      if (scale === 1 && dataUrl.startsWith("data:image/jpeg")) return resolve(dataUrl);
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", 0.9));
    };
    img.onerror = () => reject(new GenerationError("INVALID_PHOTO"));
    img.src = dataUrl;
  });
}

// Development fallback used ONLY when the backend reports no provider is
// configured (AI_PROVIDER=mock). Renders a labelled placeholder so the flow
// stays testable without spending credits.
async function composeMockPreview(originalImage, style) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const w = img.width;
      const h = img.height;
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.filter = "saturate(1.15) contrast(1.05)";
      ctx.drawImage(img, 0, 0, w, h);
      ctx.filter = "none";
      const badgeH = Math.round(Math.min(w, h) * 0.07);
      const pad = Math.round(badgeH * 0.5);
      const label = `${(style?.name || "AI PREVIEW").toUpperCase()} · PREVIEW MODE`;
      ctx.font = `700 ${Math.round(badgeH * 0.4)}px "Plus Jakarta Sans", sans-serif`;
      const tw = ctx.measureText(label).width;
      ctx.fillStyle = "rgba(15,23,42,0.85)";
      ctx.beginPath();
      ctx.roundRect(pad, h - pad - badgeH, tw + badgeH, badgeH, badgeH / 2);
      ctx.fill();
      ctx.fillStyle = "#fff";
      ctx.textBaseline = "middle";
      ctx.fillText(label, pad + badgeH / 2, h - pad - badgeH / 2 + 1);
      resolve(canvas.toDataURL("image/jpeg", 0.9));
    };
    img.onerror = () => reject(new GenerationError("INVALID_PHOTO"));
    img.src = originalImage;
  });
}

export async function generateGroomingPreview(params) {
  const { originalImage, style } = params;
  if (!originalImage) throw new GenerationError("INVALID_PHOTO");
  const start = performance.now();
  const image = await prepareImage(originalImage);

  let data;
  try {
    data = await apiFetch("/preview/generate", {
      method: "POST",
      body: {
        clientId: getClientId(),
        image,
        breed: params.breed || "",
        style: style ? { id: style.id, name: style.name, description: style.description } : null,
        bodyLength: params.bodyLength || "",
        faceStyle: params.faceStyle || "",
        earStyle: params.earStyle || "",
        tailStyle: params.tailStyle || "",
        customInstructions: params.customInstructions || "",
      },
    });
  } catch (e) {
    if (e instanceof ApiError) {
      if (e.status === 429) throw new GenerationError("LIMIT_REACHED", { reason: e.payload.reason, usage: e.payload.usage });
      if (e.status === 422) throw new GenerationError("INVALID_PHOTO");
      if (e.status === 502 || e.status === 503) throw new GenerationError("GENERATION_FAILED");
      if (e.status === 0) throw new GenerationError("NETWORK");
    }
    throw new GenerationError("SERVER_ERROR");
  }

  const previewImage = data.isMock ? await composeMockPreview(image, style) : data.previewImage;
  return {
    previewImage,
    prompt: data.prompt,
    provider: data.provider,
    isMock: !!data.isMock,
    usage: data.usage,
    durationMs: performance.now() - start,
  };
}
